import {getItemAltText} from '../../accessibility/getItemAltText';
import {getEnv} from '../../common/env/env';
import {getCommonBadge} from '../../common/getCommonBadge';
import {getSubtitleList} from '../../common/getSubtitleList';
import {CardBannerDto} from '../../common/shared/api/gateway/CardBannerDto.type';
import {ItemSeriesDto} from '../../common/shared/api/gateway/ItemSeriesDto.interface';
import {ItemSingleDto} from '../../common/shared/api/gateway/ItemSingleDto.interface';
import {getDiscountRateText} from '../../common/shared/utils/getDiscountRateText';
import {getKageUrl} from '../../common/shared/utils/getKageUrl';
import {isNilEmpty} from '../../common/shared/utils/isNilEmpty';
import {generateNumberUuid} from '../../common/shared/utils/uuid';
import {
  BadgeType,
  CardViewItem,
  CategoryType,
  EventLog,
  ItemType,
  Maybe,
  MetaInfoType,
  SectionType,
  User,
} from '../generated/types';
import {createAgeGrade, getAgeGradeVo} from './AgeGrade';
import {createCategoryType} from './CategoryType';
import {createMetaInfoType} from './MetaInfoType';
import {getCardThumbnail} from './util/getCardThumbnail';
import {getUseBookCardCover} from './util/getUseBookCardCover';

export function createCardViewItem(
  userInfo: Maybe<User>,
  param:
    | {
        rankingItemSeriesDto: ItemSeriesDto;
      }
    | {
        itemSeriesDto: ItemSeriesDto;
        metaInfoType?: Maybe<MetaInfoType>;
      }
    | {
        itemSingleDto: ItemSingleDto;
      }
    | {
        bannerItemDto: CardBannerDto;
        metaInfoType?: Maybe<MetaInfoType>;
      }
): CardViewItem {
  return 'itemSeriesDto' in param
    ? withItemSeriesDto(userInfo, param.itemSeriesDto, param.metaInfoType)
    : 'bannerItemDto' in param
      ? withBannerItemSeriesDto(userInfo, param.bannerItemDto)
      : 'rankingItemSeriesDto' in param
        ? withRankingItemSeriesDto(userInfo, param.rankingItemSeriesDto)
        : withItemSingleDto(userInfo, param.itemSingleDto);
}
function withItemSeriesDto(
  userInfo: Maybe<User>,
  itemSeriesDto: ItemSeriesDto,
  metaInfoType?: Maybe<MetaInfoType>
): CardViewItem {
  const {
    series_id,
    thumbnail,
    title,
    age_grade,
    badge,
    asset_property,
    service_property,
    operator_property,
    is_waitfree,
    business_model,
    waitfree_period_by_minute,
    category_uid,
    on_issue,
    authors,
    category,
    sub_category,
  } = itemSeriesDto;
  const categoryType = createCategoryType(category_uid);
  const {useBookCardCover, showBookCardView} =
    getUseBookCardCover(asset_property);
  const isBook =
    operator_property?.is_book || categoryType === CategoryType.Book;
  const cardThumbnail = getCardThumbnail(thumbnail, asset_property, isBook);
  const {badgeList, ageGradeBadge, statusBadge} = getCommonBadge({
    itemType: ItemType.CardView,
    badge,
    age_grade,
    is_waitfree,
    waitfree_period_by_minute: waitfree_period_by_minute || null,
    business_model,
    self_censorship: operator_property?.self_censorship,
  });

  // 대체 텍스트 일반 카드뷰: 작품, {작품명}, {BM뱃지}, {업데이트뱃지}, {연령 뱃지}, 조회수 {}, 버튼
  const altText = getItemAltText({
    title,
    badgeList,
    ageGradeBadge,
    statusBadge,
    subtitleList: getSubtitleList({
      itemType: ItemType.CardView,
      metaInfoType,
      servicePropertyDto: service_property,
      onIssueVo: on_issue,
      authors,
      isAltText: true,
      categoryName: category,
    }),
  });

  const helixImpId = operator_property?.helix_imp_id;
  const eventLog: EventLog = {
    eventMeta: {
      id: String(series_id),
      type: 'seriesId',
      provider: helixImpId ? 'helix' : '서비스',
      name: itemSeriesDto.title,
      category,
      subcategory: sub_category,
      series: itemSeriesDto.title,
      series_id: String(series_id),
    },
    viewimp_contents: helixImpId
      ? {id: String(series_id), type: 'seriesId', name: itemSeriesDto.title}
      : undefined,
    customProps: {
      rec_id: operator_property?.rec_id,
      landing_path: 'contents', // 작품홈 랜딩
      view_type: 'series_card_view',
      ...(helixImpId ? {helix_id: helixImpId, helix_yn: 'y'} : {}),
    },
  };

  return {
    id: `CardView-ItemSeries-${series_id}-${metaInfoType}`,
    type: ItemType.CardView,
    ageGrade: createAgeGrade(age_grade),
    selfCensorship: operator_property?.self_censorship,
    scheme: `kakaopage://open/content?series_id=${series_id}`,
    badgeList,
    ageGradeBadge,
    statusBadge,
    subtitleList: getSubtitleList({
      itemType: ItemType.CardView,
      metaInfoType,
      servicePropertyDto: service_property,
      onIssueVo: on_issue,
      authors,
      categoryName: category,
    }),
    title,
    altText,
    thumbnail: cardThumbnail?.startsWith('http')
      ? cardThumbnail
      : getKageUrl({
          kageUrlPrefix: getEnv().kageUrlPrefix,
          kageId: cardThumbnail,
          resolution: 'th3',
        }),
    categoryType,
    eventLog,
    discountRate: operator_property?.cash_info?.discount_rate,
    discountRateText: getDiscountRateText({
      discountRate: operator_property?.cash_info?.discount_rate,
      setDiscountRate: operator_property?.cash_info?.set_discount_rate,
    }),
    backgroundColor: asset_property?.card_set?.background_color,
    isBook: isBook || showBookCardView,
    isLegacy: isNilEmpty(asset_property?.card_set) && !useBookCardCover,
  };
}

function withRankingItemSeriesDto(
  userInfo: Maybe<User>,
  itemSeriesDto: ItemSeriesDto
): CardViewItem {
  const {
    series_id,
    title,
    thumbnail,
    badge,
    asset_property,
    service_property,
    operator_property,
    category,
    category_uid,
    sub_category,
    is_waitfree,
    waitfree_period_by_minute,
    age_grade,
    business_model,
  } = itemSeriesDto;
  const categoryType = createCategoryType(category_uid);
  const {useBookCardCover, showBookCardView} =
    getUseBookCardCover(asset_property);
  const isBook =
    operator_property?.is_book || categoryType === CategoryType.Book;
  const cardThumbnail = getCardThumbnail(thumbnail, asset_property, isBook);
  const {badgeList, ageGradeBadge, statusBadge} = getCommonBadge({
    itemType: ItemType.RankingCardView,
    badge,
    age_grade,
    is_waitfree,
    waitfree_period_by_minute: waitfree_period_by_minute || null,
    self_censorship: operator_property?.self_censorship,
    business_model,
  });

  // 대체 텍스트 랭킹 카드뷰: 작품, {작품명}, {BM뱃지}, {업데이트뱃지}, {연령 뱃지}, 조회수 {}, 버튼
  const altText = getItemAltText({
    title,
    badgeList,
    ageGradeBadge,
    statusBadge,
    rank: service_property?.rank || '',
    rankingVariation: service_property?.rank_variation,
  });

  return {
    id: `RankingCardViewItem-${series_id}-${service_property?.rank}-${service_property?.rank_variation}`,
    type: ItemType.RankingCardView,
    ageGrade: createAgeGrade(age_grade),
    selfCensorship: operator_property?.self_censorship,
    title,
    altText,
    badgeList,
    ageGradeBadge,
    statusBadge,
    thumbnail: cardThumbnail?.startsWith('http')
      ? cardThumbnail
      : getKageUrl({
          kageUrlPrefix: getEnv().kageUrlPrefix,
          kageId: cardThumbnail,
          resolution: 'th3',
        }),
    scheme: `kakaopage://open/content?series_id=${series_id}`,
    rank: service_property?.rank || '',
    rankVariation: service_property?.rank_variation,
    categoryType,
    eventLog: {
      eventMeta: {
        id: String(series_id),
        type: 'seriesId',
        name: title,
        category,
        subcategory: sub_category,
        provider: '서비스',
        series: title,
        series_id: String(series_id),
      },
      customProps: {
        rec_id: operator_property?.rec_id,
        landing_path: 'contents',
        view_type: 'series_card_view',
      },
    },
    discountRate: operator_property?.cash_info?.discount_rate,
    discountRateText: getDiscountRateText({
      discountRate: operator_property?.cash_info?.discount_rate,
      setDiscountRate: operator_property?.cash_info?.set_discount_rate,
    }),
    backgroundColor: asset_property?.card_set?.background_color,
    isBook: isBook || showBookCardView,
    isLegacy: isNilEmpty(asset_property?.card_set) && !useBookCardCover,
  };
}

function withBannerItemSeriesDto(
  userInfo: Maybe<User>,
  bannerItemDto: CardBannerDto
): CardViewItem {
  const {
    thumbnail: bannerThumbnail,
    operator_property,
    service_property,
    category,
    sub_category,
    age_grade,
    badge,
    business_model,
    meta_info_type,
    category_uid,
    caption,
    alt_text,
    asset_property,
  } = bannerItemDto;
  const categoryType = createCategoryType(category_uid);
  const {useBookCardCover, showBookCardView} =
    getUseBookCardCover(asset_property);
  const isBook =
    operator_property?.is_book || categoryType === CategoryType.Book;
  const itemSeriesDto =
    'series_property' in bannerItemDto
      ? bannerItemDto.series_property
      : undefined;
  const cardThumbnail = getCardThumbnail(
    itemSeriesDto?.thumbnail ?? bannerThumbnail,
    asset_property,
    isBook
  );
  const isEventBanner = !itemSeriesDto || operator_property?.is_event;
  const metaInfoType = createMetaInfoType(
    SectionType.CardBanner,
    meta_info_type
  );

  const helixImpId = operator_property?.helix_imp_id;
  const eventLog: EventLog = {
    eventMeta: {
      ...(isEventBanner
        ? {
            id: bannerItemDto.scheme,
            type: 'scheme',
          }
        : {
            id: String(itemSeriesDto.series_id),
            type: 'seriesId',
            name: itemSeriesDto.title,
            category,
            subcategory: sub_category,
            series: itemSeriesDto.title,
            series_id: String(itemSeriesDto.series_id),
          }),
      provider: '서비스',
    },
    viewimp_contents: {
      ...(isEventBanner
        ? {
            id: bannerItemDto.scheme,
            type: 'scheme',
          }
        : {
            id: String(itemSeriesDto.series_id),
            type: 'seriesId',
            name: itemSeriesDto.title,
          }),
      imp_provider: '서비스',
    },
    customProps: {
      event_series_id: itemSeriesDto?.series_id
        ? String(itemSeriesDto.series_id)
        : undefined,
      landing_path: itemSeriesDto ? 'contents' : 'scheme',
      view_type: 'banner_card_view',
      banner_uid: operator_property?.banner_uid
        ? String(operator_property.banner_uid)
        : undefined,
      ...(helixImpId ? {helix_id: helixImpId, helix_yn: 'y'} : {}),
    },
  };
  const {
    badgeList = [],
    ageGradeBadge = undefined,
    statusBadge = undefined,
  } = operator_property?.is_event
    ? {
        badgeList: [BadgeType.BadgeEventStatic],
      }
    : itemSeriesDto
      ? getCommonBadge({
          itemType: ItemType.CardView,
          badge,
          age_grade,
          is_waitfree: itemSeriesDto.is_waitfree,
          waitfree_period_by_minute:
            itemSeriesDto.waitfree_period_by_minute || null,
          self_censorship: operator_property?.self_censorship,
          business_model,
        }) || {}
      : {};

  return {
    id: `CardView-BannerItem-${
      operator_property?.banner_uid ?? generateNumberUuid()
    }`,
    type: ItemType.CardView,
    scheme: bannerItemDto.scheme
      ? bannerItemDto.scheme
      : itemSeriesDto
        ? `kakaopage://open/content?series_id=${itemSeriesDto.series_id}`
        : undefined,
    title: itemSeriesDto ? itemSeriesDto.title : bannerItemDto.title,
    altText: alt_text,
    ageGrade: createAgeGrade(age_grade),
    badgeList,
    ageGradeBadge,
    statusBadge,
    subtitleList:
      metaInfoType !== MetaInfoType.None
        ? getSubtitleList({
            itemType: ItemType.CardView,
            metaInfoType,
            servicePropertyDto: service_property,
            onIssueVo: itemSeriesDto?.on_issue,
            authors: bannerItemDto?.authors, // CardSection 에서는 무조건 bannerItemDto.authors 를 보도록 협의했습니다(eddy)
            categoryName: category,
          })
        : caption
          ? [caption]
          : undefined,
    thumbnail: cardThumbnail?.startsWith('http')
      ? cardThumbnail
      : getKageUrl({
          kageUrlPrefix: getEnv().kageUrlPrefix,
          kageId: cardThumbnail,
          resolution: 'th3',
        }),
    isEventBanner,
    categoryType,
    selfCensorship: operator_property?.self_censorship,
    eventLog,
    discountRate: operator_property?.cash_info?.discount_rate,
    discountRateText: getDiscountRateText({
      discountRate: operator_property?.cash_info?.discount_rate,
      setDiscountRate: operator_property?.cash_info?.set_discount_rate,
    }),
    backgroundColor: asset_property?.card_set?.background_color,
    isBook: isBook || showBookCardView,
    isLegacy: isNilEmpty(asset_property?.card_set) && !useBookCardCover,
  };
}

function withItemSingleDto(
  userInfo: Maybe<User>,
  itemSingleDto: ItemSingleDto
): CardViewItem {
  const {
    series_property,
    product_id,
    thumbnail,
    badge,
    asset_property,
    operator_property,
  } = itemSingleDto;
  const ageGradeVo = getAgeGradeVo({
    singleAgeGrade: itemSingleDto.age_grade,
    seriesAgeGrade: series_property?.age_grade,
  });
  const {
    is_waitfree = false,
    waitfree_period_by_minute,
    business_model,
  } = series_property || {};
  const categoryType = createCategoryType(series_property?.category_uid);
  const {useBookCardCover, showBookCardView} =
    getUseBookCardCover(asset_property);
  const isBook =
    operator_property?.is_book || categoryType === CategoryType.Book;
  const cardThumbnail = getCardThumbnail(thumbnail, asset_property, isBook);
  const {badgeList, ageGradeBadge, statusBadge} = getCommonBadge({
    itemType: ItemType.CardView,
    business_model,
    is_waitfree,
    waitfree_period_by_minute: waitfree_period_by_minute || null,
    age_grade: ageGradeVo,
    badge,
    self_censorship: operator_property?.self_censorship,
  });

  const eventLog: EventLog = {
    eventMeta: {
      id: String(product_id),
      type: 'productId',
      provider: '서비스',
      series: series_property?.title,
      series_id: String(series_property?.series_id),
    },
    customProps: {
      rec_id: operator_property?.rec_id,
      landing_path: 'viewer', // 뷰어 랜딩
      view_type: 'single_card_view',
    },
  };

  return {
    id: `CardView-ItemSingle-${product_id}`,
    type: ItemType.CardView,
    ageGrade: createAgeGrade(ageGradeVo),
    selfCensorship: operator_property?.self_censorship,
    scheme: `kakaopage://open/viewer?product_id=${product_id}&series_id=${series_property?.series_id}`,
    badgeList,
    ageGradeBadge,
    statusBadge,
    thumbnail: cardThumbnail?.startsWith('http')
      ? cardThumbnail
      : getKageUrl({
          kageUrlPrefix: getEnv().kageUrlPrefix,
          kageId: cardThumbnail,
          resolution: 'th3',
        }),
    eventLog,
    discountRate: operator_property?.cash_info?.discount_rate,
    discountRateText: getDiscountRateText({
      discountRate: operator_property?.cash_info?.discount_rate,
      setDiscountRate: operator_property?.cash_info?.set_discount_rate,
    }),
    backgroundColor: asset_property?.card_set?.background_color,
    isBook: isBook || showBookCardView,
    isLegacy: isNilEmpty(asset_property?.card_set) && !useBookCardCover,
  };
}
